import { useEffect, useState } from 'react'
import { useEventApi } from 'hooks/use-event-api/useEventApi'
import { FrontendEvent, ExistingTicketHolder, SocketEvent } from 'common/types'
import { useAuth } from 'pages/auth/hooks/useAuth'
import { wordPressUrl } from 'common/globalConstants'
import { useSocket } from 'hooks/use-socket/useSocket'
import moment from 'moment'

export const useSummitPost = (summitSubTitle: string) => {
  const { fetchSummitBySubTitle, fetchSummitAttendeeAndTicket } = useEventApi()
  const { backendUser } = useAuth()
  const [currentSummit, setCurrentSummit] = useState<FrontendEvent>()
  const [summitAttendeeAndTicket, setSummitAttendeeAndTicket] = useState<ExistingTicketHolder>()
  const { get, socket } = useSocket()
  const [bfutrTrigger, setBfutrTrigger] = useState<boolean>(false)

  const redirectURI = process.env.REACT_APP_REDIRECT_URI
  const registrationURI = redirectURI.replace('authenticate', 'bfutr2023/registration')
  const jobsURI = redirectURI.replace('authenticate', 'private-jobs')
  const dayOneStartTime = '2023-10-26T09:29:59'
  const dayOneEndTime = '2023-10-26T12:59:59'
  const dayTwoStartTime = '2023-10-27T13:29:59'
  const dayTwoEndTime = '2023-10-27T17:29:59'

  useEffect(() => {
    const getData = async () => {
      const summit = await fetchSummitBySubTitle(summitSubTitle)
      if (summit) {
        setCurrentSummit(summit)
        const existingTicketHolder = await fetchSummitAttendeeAndTicket(backendUser?.id, summit.id)
        setSummitAttendeeAndTicket(existingTicketHolder)
      }
    }
    getData()
  }, [summitSubTitle])

  const getCurrentTime = () => {
    const torontoTimeZone = 'America/Toronto'
    const currentDate = new Date().toLocaleDateString('en-US', { timeZone: torontoTimeZone })
    const currentTime = new Date().toLocaleTimeString('en-US', { timeZone: torontoTimeZone, hour12: false })

    // Convert the formatted date string to match the format of time parameters in isCurrentDateTimeInTimeSlots()
    const dateParts = currentDate.split('/')
    const [month, day, year] = dateParts.map(part => part.trim())

    const timeParts = currentTime.split(':')
    const [hours, minutes, seconds] = timeParts.map(part => part.trim())

    const formattedDateTime = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hours.padStart(
      2,
      '0'
    )}:${minutes.padStart(2, '0')}:${seconds.padStart(2, '0')}`
    return formattedDateTime
  }

  const isCurrentDateTimeInTimeSlots = (
    startDateTime1: string,
    endDateTime1: string,
    startDateTime2: string,
    endDateTime2: string
  ): boolean => {
    const torontoTimeZone = 'America/Toronto'

    const options: Intl.DateTimeFormatOptions = {
      timeZone: torontoTimeZone,
      timeZoneName: 'short',
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }

    const currentDateTime = new Date(getCurrentTime()).toLocaleString('en-US', options)

    const startTime1 = new Date(startDateTime1).toLocaleString('en-US', options)
    const endTime1 = new Date(endDateTime1).toLocaleString('en-US', options)

    const startTime2 = new Date(startDateTime2).toLocaleString('en-US', options)
    const endTime2 = new Date(endDateTime2).toLocaleString('en-US', options)

    if (
      (moment(currentDateTime).isAfter(moment(startTime1)) && moment(currentDateTime).isBefore(moment(endTime1))) ||
      (moment(currentDateTime).isAfter(moment(startTime2)) && moment(currentDateTime).isBefore(moment(endTime2)))
    ) {
      return true
    } else {
      return false
    }
  }

  const isCurrentDateTimeAfterDeadline = (deadline?: string): boolean => {
    const torontoTimeZone = 'America/Toronto'
    const options: Intl.DateTimeFormatOptions = {
      timeZone: torontoTimeZone,
      timeZoneName: 'short',
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }

    const currentDateTime = new Date(getCurrentTime()).toLocaleString('en-US', options)
    const deadlineDateTime = new Date(deadline ?? dayTwoEndTime).toLocaleString('en-US', options)

    return moment(currentDateTime).isAfter(moment(deadlineDateTime))
  }

  const renderCTAContent = (type: 'button' | 'link'): string => {
    const isEventTime = isCurrentDateTimeInTimeSlots(dayOneStartTime, dayOneEndTime, dayTwoStartTime, dayTwoEndTime)
    const userIsAttendingBfutr = summitAttendeeAndTicket?.attending

    if (isEventTime) {
      if (userIsAttendingBfutr) {
        return type === 'button' ? 'Join Event' : summitAttendeeAndTicket?.eventAttendee.metaData.bmConferenceUrl
      } else {
        return type === 'button' ? 'Happening Now! Get Your Pass' : registrationURI
      }
    } else {
      return type === 'button' ? 'View Event' : wordPressUrl
    }
  }

  const renderBannerContent = (type: 'button' | 'link'): string => {
    const isEventTime = isCurrentDateTimeInTimeSlots(dayOneStartTime, dayOneEndTime, dayTwoStartTime, dayTwoEndTime)
    const userIsAttendingBfutr = summitAttendeeAndTicket?.attending

    if (isEventTime) {
      if (userIsAttendingBfutr) {
        return type === 'button' ? 'Watch Main Stage w/ BFUTR Pass' : summitAttendeeAndTicket?.eventAttendee.metaData.bmConferenceUrl
      } else {
        return type === 'button' ? 'Watch Main Stage w/ BFUTR Pass' : registrationURI
      }
    } else if (isCurrentDateTimeAfterDeadline()) {
      return type === 'button' ? 'Share BFUTR photos in Tea Spot!' : undefined
    } else if (userIsAttendingBfutr && isCurrentDateTimeAfterDeadline(dayOneEndTime)) {
      return type === 'button' ? 'Explore the Latest Job Postings' : jobsURI
    } else if (!userIsAttendingBfutr && isCurrentDateTimeAfterDeadline(dayOneEndTime)) {
      return type === 'button' ? 'Watch Main Stage w/ BFUTR Pass' : registrationURI
    } else {
      return type === 'button' ? 'BFUTR Passes Available' : registrationURI
    }
  }

  useEffect(() => {
    get(SocketEvent.BfutrEventTimeTrigger, () => {
      setBfutrTrigger(!bfutrTrigger)
    })
    return () => {
      socket.off(SocketEvent.BfutrEventTimeTrigger)
    }
  }, [bfutrTrigger])

  return { currentSummit, summitAttendeeAndTicket, renderCTAContent, renderBannerContent, isCurrentDateTimeAfterDeadline } as const
}
